/**
 * SleepyCat first-party attribution capture (UTM / click IDs)
 *
 * Problem this solves: inbound UTMs only live in the landing URL and Shopify's
 * native pixel. There was no durable first-party copy — so order-level
 * attribution (for apps, Flow, or the Sheets/Code.gs pipeline) was impossible,
 * and any UTM dropped between landing and checkout was unrecoverable.
 *
 * What it does:
 *   1. On landing, reads utm_* / gclid / fbclid / wbraid / gbraid from the URL.
 *   2. Persists first-touch (never overwritten) and last-touch (always refreshed)
 *      to a first-party cookie + localStorage mirror.
 *   3. Writes them onto the CART as cart-level attributes (via /cart/update.js),
 *      so Shopify carries them onto the ORDER automatically.
 *
 * Design constraints (see plan):
 *   - Never touches an add-to-cart fetch. Cart attributes MERGE — they don't
 *     clear line items or other attributes.
 *   - Path-independent: the sync runs on idle regardless of which add-to-cart
 *     path the shopper uses (form POST, upsell, auto-add, bundle), and also
 *     re-syncs on the theme's `cartUpdate` pubsub event as a backup.
 *   - All network calls are fire-and-forget; failures never affect the UI.
 *   - No-op in the theme editor (Shopify.designMode), mirroring analytics.js.
 *
 * @version 1.0.0
 */
(function () {
  'use strict';

  // Don't capture or write to the cart inside the theme editor.
  if (window.Shopify && window.Shopify.designMode) return;

  var ATTR_KEYS = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
    'gclid', 'fbclid', 'wbraid', 'gbraid'
  ];

  var COOKIE_FIRST = 'sc_attrib_first';
  var COOKIE_LAST = 'sc_attrib_last';
  var SYNC_FLAG = 'sc_attrib_synced';      // sessionStorage — one cart write per session
  var COOKIE_MAX_AGE = 60 * 60 * 24 * 30;  // 30 days
  var MAX_VALUE_LEN = 255;                 // guard against pathological query strings

  // ---- storage helpers (cookie primary, localStorage mirror, both defensive) ----

  function setCookie(name, value) {
    try {
      document.cookie =
        name + '=' + encodeURIComponent(value) +
        '; Max-Age=' + COOKIE_MAX_AGE + '; Path=/; SameSite=Lax';
    } catch (e) { /* cookies disabled — localStorage mirror still works */ }
  }

  function getCookie(name) {
    try {
      var match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
      return match ? decodeURIComponent(match[1]) : null;
    } catch (e) { return null; }
  }

  function lsGet(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  }

  function lsSet(key, value) {
    try { window.localStorage.setItem(key, value); } catch (e) { /* private mode / quota */ }
  }

  function readStore(key) {
    return getCookie(key) || lsGet(key);
  }

  function writeStore(key, value) {
    setCookie(key, value);
    lsSet(key, value);
  }

  function safeParse(json) {
    if (!json) return null;
    try { return JSON.parse(json); } catch (e) { return null; }
  }

  // ---- capture ----

  // Returns a touch object { key: value, ... } for attribution params present
  // in the current URL, or null if none. Also records the landing page.
  function readTouchFromUrl() {
    var params;
    try { params = new URLSearchParams(window.location.search); }
    catch (e) { return null; }

    var touch = {};
    var found = false;
    for (var i = 0; i < ATTR_KEYS.length; i++) {
      var k = ATTR_KEYS[i];
      var v = params.get(k);
      if (v) {
        touch[k] = String(v).slice(0, MAX_VALUE_LEN);
        found = true;
      }
    }
    if (!found) return null;

    touch.landing_page = String(location.pathname + location.search).slice(0, MAX_VALUE_LEN);
    var ref = document.referrer || '';
    if (ref) touch.referrer = ref.slice(0, MAX_VALUE_LEN);
    touch.ts = new Date().toISOString();
    return touch;
  }

  function captureOnLanding() {
    var touch = readTouchFromUrl();
    if (!touch) return;

    var json = JSON.stringify(touch);

    // Last-touch: always refresh.
    writeStore(COOKIE_LAST, json);

    // First-touch: set only if we've never recorded one.
    if (!readStore(COOKIE_FIRST)) {
      writeStore(COOKIE_FIRST, json);
    }

    // New attribution arrived — allow a fresh sync to the cart this session.
    try { window.sessionStorage.removeItem(SYNC_FLAG); } catch (e) {}
  }

  // ---- build cart attributes from stored touches ----

  // Flattens stored first/last touch into a flat string map for cart attributes.
  // Last-touch uses the plain utm_* keys (what attribution apps expect);
  // first-touch is prefixed so both are queryable on the order.
  function buildAttributes() {
    var last = safeParse(readStore(COOKIE_LAST));
    var first = safeParse(readStore(COOKIE_FIRST));
    if (!last && !first) return null;

    var attrs = {};
    var i, k;

    if (last) {
      for (i = 0; i < ATTR_KEYS.length; i++) {
        k = ATTR_KEYS[i];
        if (last[k]) attrs[k] = last[k];
      }
      if (last.landing_page) attrs.last_landing_page = last.landing_page;
    }

    if (first) {
      for (i = 0; i < ATTR_KEYS.length; i++) {
        k = ATTR_KEYS[i];
        if (first[k]) attrs['first_' + k] = first[k];
      }
      if (first.landing_page) attrs.first_landing_page = first.landing_page;
      if (first.referrer) attrs.first_referrer = first.referrer;
    }

    return Object.keys(attrs).length ? attrs : null;
  }

  // ---- sync to cart (fire-and-forget, once per session) ----

  function alreadySynced() {
    try { return window.sessionStorage.getItem(SYNC_FLAG) === '1'; }
    catch (e) { return false; }
  }

  function markSynced() {
    try { window.sessionStorage.setItem(SYNC_FLAG, '1'); } catch (e) {}
  }

  function syncToCart() {
    if (alreadySynced()) return;

    var attrs = buildAttributes();
    if (!attrs) return;

    var updateUrl = (window.routes && window.routes.cart_update_url) || '/cart/update.js';

    // Reuse the theme's request config helper when available; otherwise inline it.
    var cfg = (typeof fetchConfig === 'function')
      ? fetchConfig()
      : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
    cfg.body = JSON.stringify({ attributes: attrs });

    fetch(updateUrl, cfg)
      .then(function (res) { if (res && res.ok) markSynced(); })
      .catch(function () { /* never block the UI on attribution */ });
  }

  // ---- wire up ----

  captureOnLanding();

  // Primary, path-independent sync once the page is idle. Cart attributes
  // persist on the cart token even when written before the first add, so the
  // order is attributed regardless of which add-to-cart path is used.
  function scheduleSync() {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(syncToCart, { timeout: 2000 });
    } else {
      setTimeout(syncToCart, 1200);
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleSync, { once: true });
  } else {
    scheduleSync();
  }

  // Backup: re-sync after engagement if a new touch cleared the session flag.
  // Uses the theme's existing pubsub (constants.js / pubsub.js) when present.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, syncToCart);
  }
})();
